"use client";

// frontend/components/settings/AdminAuditLogTab.tsx
//
// 어드민 전용 — 조직 전체 감사 로그 (개인 문서 제외)
// 액션: 문서 생성 · 이름 변경 · 문서 이동 · 문서 삭제 · 문서 복구 · 본문 수정

import { useState, useEffect, useCallback } from "react";
import { Search, ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { API_URL, authHeaders } from "../../utils/api";
import { formatFullDate } from "../../utils/dateUtils";

interface AuditLog {
  id: string;
  userId: string | null;
  userName: string;
  actionType: string;
  createdAt: string;
  document: { id: string; title: string; workspaceType: string; accessDept: string | null } | null;
}

interface AuditLogsResponse {
  total: number;
  page: number;
  limit: number;
  logs: AuditLog[];
}

const ACTION_TYPES = ["전체", "문서 생성", "이름 변경", "문서 이동", "문서 삭제", "문서 복구", "본문 수정"];

const ACTION_BADGE: Record<string, string> = {
  "문서 생성": "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  "이름 변경": "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  "문서 이동": "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  "문서 삭제": "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  "문서 복구": "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  "본문 수정": "bg-gray-100 text-gray-600 dark:bg-zinc-800 dark:text-zinc-400",
};

interface Props {
  accessToken: string;
}

export default function AdminAuditLogTab({ accessToken }: Props) {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  // 필터
  const [userNameFilter, setUserNameFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("전체");
  const [fromFilter, setFromFilter] = useState("");
  const [toFilter, setToFilter] = useState("");

  const limit = 50;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const fetch_ = useCallback(async (p: number) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: String(limit) });
      if (actionFilter !== "전체") params.set("actionType", actionFilter);
      if (fromFilter) params.set("from", new Date(fromFilter).toISOString());
      if (toFilter) {
        const d = new Date(toFilter);
        d.setHours(23, 59, 59, 999);
        params.set("to", d.toISOString());
      }

      const res = await globalThis.fetch(`${API_URL}/system/audit-logs?${params}`, {
        headers: authHeaders(accessToken),
      });
      if (!res.ok) throw new Error();
      const data: AuditLogsResponse = await res.json();

      // userName 클라이언트 필터 (API가 이름 필터를 지원하지 않으므로 FE에서 처리)
      const filtered = userNameFilter.trim()
        ? data.logs.filter(l => l.userName.includes(userNameFilter.trim()))
        : data.logs;

      setLogs(filtered);
      setTotal(data.total);
    } catch {
      setLogs([]);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, actionFilter, fromFilter, toFilter, userNameFilter]);

  useEffect(() => {
    setPage(1);
    fetch_(1);
  }, [actionFilter, fromFilter, toFilter]);

  const handleSearch = () => { setPage(1); fetch_(1); };
  const handlePageChange = (p: number) => { setPage(p); fetch_(p); };

  const inputCls = "h-8 px-2.5 text-xs border border-gray-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-800 text-gray-700 dark:text-zinc-200 focus:outline-none focus:ring-1 focus:ring-[#2EAADC]";

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-800 dark:text-zinc-100">감사 로그</h3>
        <button
          onClick={() => fetch_(page)}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-500 dark:text-zinc-400 hover:text-gray-700 dark:hover:text-zinc-200 border border-gray-200 dark:border-zinc-700 rounded-lg transition-colors"
        >
          <RefreshCw size={12} className={isLoading ? "animate-spin" : ""} />
          새로고침
        </button>
      </div>

      {/* 필터 바 */}
      <div className="flex flex-wrap items-center gap-2">
        {/* 사용자 이름 필터 */}
        <div className="flex items-center gap-1.5">
          <input
            className={`${inputCls} w-28`}
            placeholder="사용자 이름"
            value={userNameFilter}
            onChange={e => setUserNameFilter(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleSearch()}
          />
          <button onClick={handleSearch} className="h-8 px-2 rounded-lg border border-gray-200 dark:border-zinc-700 hover:bg-gray-50 dark:hover:bg-zinc-700 transition-colors">
            <Search size={13} className="text-gray-400" />
          </button>
        </div>

        {/* 액션 필터 */}
        <select
          className={`${inputCls} pr-6`}
          value={actionFilter}
          onChange={e => setActionFilter(e.target.value)}
        >
          {ACTION_TYPES.map(a => <option key={a}>{a}</option>)}
        </select>

        {/* 날짜 범위 */}
        <input type="date" className={inputCls} value={fromFilter} onChange={e => setFromFilter(e.target.value)} />
        <span className="text-xs text-gray-400">~</span>
        <input type="date" className={inputCls} value={toFilter} onChange={e => setToFilter(e.target.value)} />

        {/* 필터 초기화 */}
        {(userNameFilter || actionFilter !== "전체" || fromFilter || toFilter) && (
          <button
            onClick={() => { setUserNameFilter(""); setActionFilter("전체"); setFromFilter(""); setToFilter(""); }}
            className="text-xs text-gray-400 dark:text-zinc-500 hover:text-gray-600 dark:hover:text-zinc-300 transition-colors"
          >
            초기화
          </button>
        )}

        <span className="ml-auto text-xs text-gray-400 dark:text-zinc-500">
          총 {total.toLocaleString()}건
        </span>
      </div>

      {/* 테이블 */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-zinc-700">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 dark:bg-zinc-800 border-b border-gray-200 dark:border-zinc-700">
              <th className="px-3 py-2.5 text-left font-semibold text-gray-500 dark:text-zinc-400 whitespace-nowrap">시간</th>
              <th className="px-3 py-2.5 text-left font-semibold text-gray-500 dark:text-zinc-400">사용자</th>
              <th className="px-3 py-2.5 text-left font-semibold text-gray-500 dark:text-zinc-400">액션</th>
              <th className="px-3 py-2.5 text-left font-semibold text-gray-500 dark:text-zinc-400">문서 제목</th>
              <th className="px-3 py-2.5 text-left font-semibold text-gray-500 dark:text-zinc-400 whitespace-nowrap">스페이스</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-3 py-8 text-center text-gray-400 dark:text-zinc-500">불러오는 중...</td>
              </tr>
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-8 text-center text-gray-400 dark:text-zinc-500">조회된 로그가 없습니다.</td>
              </tr>
            ) : logs.map(log => (
              <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-zinc-800/50 transition-colors">
                <td className="px-3 py-2 whitespace-nowrap text-gray-500 dark:text-zinc-500 tabular-nums">
                  {formatFullDate(log.createdAt)}
                </td>
                <td className="px-3 py-2 font-medium text-gray-700 dark:text-zinc-200 whitespace-nowrap">
                  {log.userName}
                </td>
                <td className="px-3 py-2">
                  <span className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-medium whitespace-nowrap ${ACTION_BADGE[log.actionType] ?? "bg-gray-100 text-gray-600 dark:bg-zinc-800 dark:text-zinc-400"}`}>
                    {log.actionType}
                  </span>
                </td>
                <td className="px-3 py-2 max-w-48 wrap-break-word text-gray-700 dark:text-zinc-200">
                  {log.document?.title ?? "(삭제된 문서)"}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-gray-500 dark:text-zinc-500">
                  {log.document?.workspaceType === "TEAM"
                    ? `팀 (${log.document.accessDept?.split(",")[0] ?? ""})`
                    : log.document?.workspaceType ?? "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => handlePageChange(page - 1)}
            disabled={page <= 1}
            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft size={14} />
          </button>
          <span className="text-xs text-gray-500 dark:text-zinc-400">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(page + 1)}
            disabled={page >= totalPages}
            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
